import React, { useEffect, useState, useRef, useContext } from "react";
import { useSnackbar } from "notistack";
import currencyData from '../../utils/currency-codes.json';
import {
  Grid, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableFooter, Button, Select, MenuItem, CircularProgress, Typography, TextField,
  Autocomplete
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import LooksOneIcon from '@mui/icons-material/LooksOne';
import LooksTwoIcon from '@mui/icons-material/LooksTwo';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AttachmentIcon from '@mui/icons-material/Attachment';

import ButtonOutlined from "../StyledComponents/ButtonOutlined";
import ButtonContained from "../StyledComponents/ButtonContained";
import axios from 'axios';
import { createCanvas, loadImage } from 'canvas';
import httpRequest from "../../httpRequest";
import UserContext from "../../context/user-context";

const AddExpense = ({ onExpenseAdded }) => {
  const { enqueueSnackbar } = useSnackbar();
  const userCtx = useContext(UserContext);
  const { employeeData } = userCtx;

  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedFunction, setSelectedFunction] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [employeeCode, setEmployeeCode] = useState('');
  const [positionId, setPositionId] = useState(null);
  const [monthYear, setMonthYear] = useState('');
  const [baseCurrency, setBaseCurrency] = useState('');
  const [companyBaseCurrencies, setCompanyBaseCurrencies] = useState({});
  const [conversionRates, setConversionRates] = useState({});
  const [loadingRates, setLoadingRates] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [balance, setBalance] = useState(null);
  const [newValue, setNewValue] = useState('');
  const [open, setOpen] = useState(false);
  const [companyLoaded, setCompanyLoaded] = useState(false);

  const PDFJS = require('pdfjs-dist/webpack');

  const currencyOptions = Object.keys(currencyData).map(code => ({
    code,
    ...currencyData[code],
  }));

  const getCurrentMonthYear = () => {
    const date = new Date();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${year}`;
  };

  useEffect(() => {
    const initializeData = async () => {
      if (employeeData && employeeData.company_id) {
        try {
          console.log('Initializing with employeeData company_id:', employeeData.company_id);
          setCompanies(employeeData.companies);
          setFunctions(employeeData.functions);
          setSelectedCompany(employeeData.company_id);
          setSelectedFunction(employeeData.function_id);
          setPositionId(employeeData.position_id);
          setEmployeeCode(employeeData.employee_code);

          const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies?id=${employeeData.company_id}`);
          const { base_currency, balance } = response.data.company;
          console.log('Initial company data:', response.data.company);
          
          setBaseCurrency(base_currency);
          setBalance(balance);
          setCompanyLoaded(true);
          
          if (balance === 0) {
            enqueueSnackbar("Your credits have finished. Please contact support.", { variant: "warning" });
          }
        } catch (error) {
          console.error("Error initializing company data:", error);
          enqueueSnackbar("Error fetching company data", { variant: "error" });
        }
      }
    };
    initializeData();
  }, [employeeData]);

  useEffect(() => {
    const fetchBaseCurrency = async () => {
      if (selectedCompany && companyLoaded) {
        try {
          console.log('Fetching company data for ID:', selectedCompany);
          const response = await httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/get-companies?id=${selectedCompany}`);
          const { base_currency, balance } = response.data.company;
          console.log('Updated company data:', response.data.company);
          
          setBaseCurrency(base_currency);
          setBalance(balance);
          
          if (balance === 0) {
            enqueueSnackbar("Your credits have finished. Please contact support.", { variant: "warning" });
          }
        } catch (error) {
          console.error("Error fetching company data:", error);
          enqueueSnackbar("Error fetching company data", { variant: "error" });
        }
      }
    };
    fetchBaseCurrency();
  }, [selectedCompany, companyLoaded]);

  const fetchConversionRates = async (currencyCode) => {
    if (loadingRates || !currencyCode) return;

    setLoadingRates(true);
    try {
      console.log('Fetching conversion rates for:', currencyCode);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';
      const response = await axios.get(
        `${backendUrl}/proxy/exchange-rates/${currencyCode}`
      );
      const data = response.data;

      if (data.result === "error") {
        throw new Error(data["error-type"]);
      }

      console.log('Rates received for:', currencyCode);
      setConversionRates(data.conversion_rates);
    } catch (error) {
      console.error("Error fetching conversion rates:", error.message);
      let errorMessage = "Error fetching conversion rates";
      if (error.message === "unsupported-code") {
        errorMessage = `Currency ${currencyCode} is not supported`;
      } else if (error.message === "invalid-key") {
        errorMessage = "Invalid API key for exchange rate service";
      } else if (error.message === "quota-reached") {
        errorMessage = "Exchange rate API quota exceeded";
      }
      enqueueSnackbar(errorMessage, { variant: "error" });
      setConversionRates({});
    } finally {
      setLoadingRates(false);
    }
  };

  useEffect(() => {
    if (baseCurrency && companyLoaded) {
      console.log('Triggering rate fetch with baseCurrency:', baseCurrency);
      fetchConversionRates(baseCurrency);
    }
  }, [baseCurrency, companyLoaded]);

  const fetchData = async () => {
    try {
      const [categoriesRes] = await Promise.all([
        httpRequest.get(`${process.env.REACT_APP_BACKEND_URL}/categories`)
      ]);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      enqueueSnackbar("Error fetching data", { variant: "error" });
    } finally {
      setLoadingCategories(false);
    }
  };

  useEffect(() => {
    fetchData();
    setMonthYear(getCurrentMonthYear());
  }, []);

  const isValidDate = (dateString) => {
    const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
    return regex.test(dateString);
  };

  const convertPdfToImage = async (pdfFile) => {
    const pdfData = await pdfFile.arrayBuffer();
    const pdf = await PDFJS.getDocument({ data: pdfData }).promise;

    const images = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 2.0 });

      const canvas = createCanvas(viewport.width, viewport.height);
      const context = canvas.getContext('2d');

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await page.render(renderContext).promise;
      const image = canvas.toDataURL("image/png");
      images.push(image);
    }
    return images;
  };

  const joinImages = async (images) => {
    const imageElements = await Promise.all(images.map(src => loadImage(src)));

    const totalHeight = imageElements.reduce((acc, img) => acc + img.height, 0);
    const maxWidth = Math.max(...imageElements.map(img => img.width));

    const canvas = createCanvas(maxWidth, totalHeight);
    const context = canvas.getContext('2d');

    let currentY = 0;
    for (let img of imageElements) {
      context.drawImage(img, 0, currentY, img.width, img.height);
      currentY += img.height;
    }

    return canvas.toDataURL("image/png");
  };

  const extractTextFromImage = async (base64Data) => {
    const categoryList = categories.map(category => category.expense_category).join(", ");
    const promptContent = `Given the following expense image, identify the type of expense the image is, and expense categories are: ${categoryList}. Only return filled JSON strictly in this format (expense date in DD/MM/YYYY and currency in code eg. SAR, USD, EUR etc.): { expense_category: '', expense_date: '', expense_name: '', expense_description: '', expense_amount: '', currency: '' }:`;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';
      const payload = {
        contents: [{
          parts: [
            { "text": promptContent },
            { "inline_data": { "mime_type": "image/png", "data": base64Data } }
          ]
        }]
      };

      const response = await axios.post(`${backendUrl}/proxy/gemini/generate-content`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const geminiResponse = response.data.candidates[0].content.parts[0].text;
      
      const jsonStart = geminiResponse.indexOf('{');
      const jsonEnd = geminiResponse.lastIndexOf('}') + 1;
      const jsonString = geminiResponse.substring(jsonStart, jsonEnd);
      return JSON.parse(jsonString);
    } catch (error) {
      console.error("Error extracting text from image:", error);
      throw error;
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setUploadedFiles(files);
    setIsFileUploaded(true);
  };

  const handleNext = async () => {
    if (uploadedFiles.length > 0 && selectedCompany && selectedFunction) {
      setLoading(true);
      const newExpenses = [];
      let processedCount = 0;
      let errors = 0;

      const processFile = async (file) => {
        if (file.type === 'application/pdf') {
          const images = await convertPdfToImage(file);
          const combinedImage = await joinImages(images);
          const base64Image = combinedImage.split(',')[1];

          const expenseData = await extractTextFromImage(base64Image);
          return { ...expenseData, file: file, month_year: monthYear };
        } else {
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
          });

          const expenseData = await extractTextFromImage(base64Data);
          return { ...expenseData, file: file, month_year: monthYear };
        }
      };

      for (const file of uploadedFiles) {
        try {
          const expenseData = await processFile(file);
          
          if (expenseData && isValidExpenseData(expenseData)) {
            if (!currencyOptions.some(option => option.code === expenseData.currency)) {
              expenseData.currency = '';
            }

            if (!isValidDate(expenseData.expense_date)) {
              expenseData.expense_date = '';
            }

            const categoryName = expenseData.expense_category;
            const matchedCategory = categories.find(category => category.expense_category === categoryName);
            
            const expenseWithCategory = {
              ...expenseData,
              expense_category_id: matchedCategory ? matchedCategory.id : '',
              file: file,
              month_year: monthYear
            };
            newExpenses.push(expenseWithCategory);
          } else {
            console.warn(`Invalid or malformed data for file: ${file.name}`);
            errors++;
          }
        } catch (error) {
          console.error("Error processing file:", error);
          errors++;
        } finally {
          processedCount++;
          
          if (processedCount === uploadedFiles.length) {
            if (errors > 0) {
              enqueueSnackbar("Some files had issues and were not processed correctly.", { variant: "warning" });
            }
            setExpenses(newExpenses);
            setLoading(false);
            setShowTable(true);
          }
        }
      }
    } else {
      enqueueSnackbar("Please select a company and function", { variant: "warning" });
    }
  };

  const isValidExpenseData = (data) => {
    try {
      const { expense_category, expense_date, expense_name, expense_description, expense_amount, currency } = data;
      return (
        typeof expense_category === 'string' &&
        typeof expense_date === 'string' &&
        typeof expense_name === 'string' &&
        typeof expense_description === 'string' &&
        typeof expense_amount === 'string' &&
        typeof currency === 'string'
      );
    } catch {
      return false;
    }
  };

  const handleAddFile = async (event) => {
    setLoading(true);
    try {
      const files = Array.from(event.target.files);
      const newExpenses = [];

      for (const file of files) {
        if (file.type === 'application/pdf') {
          const images = await convertPdfToImage(file);
          const combinedImage = await joinImages(images);
          const base64Image = combinedImage.split(',')[1];

          const expenseData = await extractTextFromImage(base64Image);

          const categoryName = expenseData.expense_category;
          const matchedCategory = categories.find(category => category.expense_category === categoryName);

          const expenseWithCategory = {
            ...expenseData,
            expense_category_id: matchedCategory ? matchedCategory.id : '',
            file: file,
            month_year: monthYear
          };
          newExpenses.push(expenseWithCategory);
        } else {
          const base64Data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = reject;
          });

          const expenseData = await extractTextFromImage(base64Data);

          const categoryName = expenseData.expense_category;
          const matchedCategory = categories.find(category => category.expense_category === categoryName);

          const expenseWithCategory = {
            ...expenseData,
            expense_category_id: matchedCategory ? matchedCategory.id : '',
            file: file,
            month_year: monthYear
          };
          newExpenses.push(expenseWithCategory);
        }
      }

      setExpenses([...expenses, ...newExpenses]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (index, field, value) => {
    let isError = false;
    if (field === 'currency') {
      isError = !currencyOptions.some(option => option.code === value);
    } else if (field === 'expense_date') {
      const date = new Date(value);
      value = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
    }
    const updatedExpenses = [...expenses];
    updatedExpenses[index][field] = value;
    updatedExpenses[index].isError = field === 'currency' ? isError : updatedExpenses[index].isError;
    setExpenses(updatedExpenses);
  };

  const handleGlobalChange = (field, value) => {
    if (field === 'company') {
      setSelectedCompany(value);
      if (companyBaseCurrencies[value]) {
        setBaseCurrency(companyBaseCurrencies[value]);
      }
    } else if (field === 'function') {
      setSelectedFunction(value);
    }
    const updatedExpenses = expenses.map(expense => ({
      ...expense,
      [`${field}_id`]: value
    }));
    setExpenses(updatedExpenses);
  };

  const openFileInNewTab = (file) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, "_blank", "width=600,height=800");
  };
  
  const handleClickOpen = (cell, value, index) => {
    setEditingCell(cell);
    setNewValue(value);
    setEditingIndex(index);
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
  };
  
  const handleSave = () => {
    handleInputChange(editingIndex, editingCell, newValue);
    handleClose();
  };

  const calculateBaseAmount = (amount, currency) => {
    if (!conversionRates || Object.keys(conversionRates).length === 0) {
      enqueueSnackbar(`Conversion rates for ${baseCurrency} not available.`, { variant: "error" });
      return 0;
    }

    if (!conversionRates[currency]) {
      enqueueSnackbar(`Invalid or missing conversion rate for ${currency}.`, { variant: "error" });
      return 0;
    }

    const conversionRate = conversionRates[currency];
    const baseAmount = (amount / conversionRate).toFixed(2);
    return baseAmount;
  };

  const handleDeleteExpense = (index) => {
    const updatedExpenses = expenses.filter((_, idx) => idx !== index);
    setExpenses(updatedExpenses);
  };

  const handleCategoryChange = (index, categoryId) => {
    const updatedExpenses = expenses.map((expense, idx) =>
      idx === index ? { ...expense, expense_category_id: categoryId } : expense
    );
    setExpenses(updatedExpenses);
  };

  const handleCancel = () => {
    setUploadedFiles([]);
    setExpenses([]);
    setShowTable(false);
    setIsFileUploaded(false);
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const addExpenses = async () => {
    setSubmitting(true);
    if (expenses.length === 0) {
      enqueueSnackbar("No expenses to submit", { variant: "error" });
      setLoading(false);
      return;
    }

    try {
      const hasEmptyFields = expenses.some(expense =>
        !expense.expense_name || !expense.expense_date || !expense.expense_amount || !expense.currency || !expense.expense_category_id
      );
      if (hasEmptyFields) {
        setExpenses(expenses.map(expense => ({
          ...expense,
          isError: !expense.expense_name || !expense.expense_date || !expense.expense_amount || !expense.currency || !expense.expense_category_id
        })));
        enqueueSnackbar("Please fill out all required fields", { variant: "error" });
        setSubmitting(false);
        return;
      }

      const updatedExpenses = await Promise.all(expenses.map(expense => {
        return new Promise((resolve, reject) => {
          if (expense.file) {
            const reader = new FileReader();
            reader.readAsDataURL(expense.file);
            reader.onload = () => {
              const base64String = reader.result.split(',')[1];
              const isImage = expense.file.type.startsWith('image/');
              const isPdf = expense.file.type === 'application/pdf';

              const baseAmount = calculateBaseAmount(expense.expense_amount, expense.currency);

              resolve({
                ...expense,
                base_amount: baseAmount,
                image_file: isImage ? base64String : null,
                pdf_file: isPdf ? base64String : null,
                file: undefined
              });
            };
            reader.onerror = reject;
          } else {
            const baseAmount = calculateBaseAmount(expense.expense_amount, expense.currency);
            resolve({
              ...expense,
              base_amount: baseAmount,
            });
          }
        });
      }));

      await httpRequest.post(`${process.env.REACT_APP_BACKEND_URL}/add-expenses`, {
        employee_code: employeeCode,
        company_id: selectedCompany,
        function_id: selectedFunction,
        position_id: positionId,
        month_year: monthYear,
        base_currency: baseCurrency,
        expenses: updatedExpenses
      });

      enqueueSnackbar("Expenses added successfully!", { variant: "success" });
      setExpenses([]);
      setShowTable(false);
      setIsFileUploaded(false);
      onExpenseAdded();
    } catch (error) {
      console.error("Error adding expenses:", error);
      enqueueSnackbar("Error adding expenses", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {!showTable && (
        <Paper elevation={3} sx={{ p: 2, borderRadius: 1, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.5)', marginTop: '16px', marginLeft: '50%', transform: 'translateX(-50%)' }}>
          <Box sx={{ mb: 2 }}>
            <Select
              fullWidth
              value={selectedCompany}
              onChange={(e) => handleGlobalChange('company', e.target.value)}
              displayEmpty
              sx={{ mb: 2 }}
            >
              <MenuItem value="" disabled>Select Company</MenuItem>
              {companies.map(company => (
                <MenuItem key={company.id} value={company.id}>
                  {company.company_name}
                </MenuItem>
              ))}
            </Select>

            <Select
              fullWidth
              value={selectedFunction}
              onChange={(e) => handleGlobalChange('function', e.target.value)}
              displayEmpty
              sx={{ mb: 2 }}
            >
              <MenuItem value="" disabled>Select Function</MenuItem>
              {functions.map(func => (
                <MenuItem key={func.id} value={func.id}>
                  {func.function_name}
                </MenuItem>
              ))}
            </Select>

            <TextField
              fullWidth
              label="Month & Year"
              value={monthYear}
              onChange={(e) => setMonthYear(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="MM/YYYY"
            />

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <ButtonContained
                variant="contained"
                onClick={handleButtonClick}
                style={{ paddingRight: '10px', paddingLeft: '10px' }}
                {...(balance === 0 || loading ? { disabled: true } : {})}
              >
                <LooksOneIcon /> Upload File(s)
                <input
                  type="file"
                  hidden
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
              </ButtonContained>

              <ButtonOutlined
                variant="contained"
                color="primary"
                style={{ marginLeft: 8, paddingRight: '10px', paddingLeft: '10px' }}
                onClick={handleNext}
                {...(!isFileUploaded || loading ? { disabled: true } : {})}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <LooksTwoIcon style={{ marginRight: '8px' }} />
                    Next
                  </>
                )}
              </ButtonOutlined>
            </Box>
          </Box>
        </Paper>
      )}

      {showTable && (
        <>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 1, backdropFilter: 'blur(10px)', backgroundColor: 'rgba(255, 255, 255, 0.5)', marginTop: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 style={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif" }}>Expense Sheet</h1>
              <div style={{ textAlign: 'right' }}>
                <h3 style={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif" }}>{monthYear}</h3>
                <h6 style={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif" }}>
                  Function:{' '}
                  {functions.find(func => func.id === selectedFunction)?.function_name}
                </h6>
                <h6 style={{ color: '#354a5f', fontFamily: "SapFiori72, sans-serif", marginBottom: '20px' }}>
                  Company:{' '}
                  {companies.find(company => company.id === selectedCompany)?.company_name}
                </h6>
              </div>
            </div>

            <TableContainer sx={{ border: '2px solid #354a5f', borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: '#354a5f' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Date</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Expense Name</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Description</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: "right" }}>Amount</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Currency</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", textAlign: "right" }}>Base Amount</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Base Currency</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Category</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif" }}>Comments</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", width: '100px' }}>Attachment</TableCell>
                      <TableCell sx={{ color: 'white', fontFamily: "SapFiori72, sans-serif", width: '100px' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenses.map((expense, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            type="date"
                            value={
                              isValidDate(expense.expense_date)
                                ? new Date(expense.expense_date.split('/').reverse().join('-')).toISOString().split('T')[0]
                                : ''
                            }
                            onChange={(e) => handleInputChange(index, 'expense_date', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            error={expense.isError && !expense.expense_date}
                            variant="standard"
                          />
                        </TableCell>
                        <TableCell onClick={() => handleClickOpen('expense_name', expense.expense_name, index)}>
                          <TextField
                            fullWidth
                            sx={{ minWidth: 100 }}
                            variant="standard"
                            value={expense.expense_name}
                            onChange={(e) => handleInputChange(index, 'expense_name', e.target.value)}
                            error={expense.isError && !expense.expense_name}
                          />
                        </TableCell>
                        <TableCell onClick={() => handleClickOpen('expense_description', expense.expense_description, index)}>
                          <TextField
                            variant="standard"
                            fullWidth
                            sx={{ minWidth: 200 }}
                            value={expense.expense_description}
                            onChange={(e) => handleInputChange(index, 'expense_description', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            variant="standard"
                            fullWidth
                            sx={{ minWidth: 100 }}
                            type="number"
                            value={expense.expense_amount}
                            onChange={(e) => handleInputChange(index, 'expense_amount', e.target.value)}
                            error={expense.isError && !expense.expense_amount}
                            InputProps={{
                              sx: { '& input': { textAlign: 'right' } }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            options={currencyOptions.map(option => option.code)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="standard"
                                fullWidth
                                sx={{ minWidth: 100 }}
                                error={expense.isError && !expense.currency}
                              />
                            )}
                            value={expense.currency}
                            onChange={(e, newValue) => handleInputChange(index, 'currency', newValue)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", textAlign: "right", fontSize: '16px' }}>
                          {expense.currency ? calculateBaseAmount(expense.expense_amount, expense.currency) : ''}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", fontSize: '16px' }}>
                          {baseCurrency}
                        </TableCell>
                        <TableCell>
                          <Select
                            fullWidth
                            variant="standard"
                            value={expense.expense_category_id || ''}
                            onChange={(e) => handleCategoryChange(index, e.target.value)}
                            displayEmpty
                            error={expense.isError && !expense.expense_category_id}
                          >
                            <MenuItem value="" disabled>Select Category</MenuItem>
                            {categories.map(category => (
                              <MenuItem key={category.id} value={category.id}>
                                {category.expense_category}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell onClick={() => handleClickOpen('comments', expense.comments || '', index)}>
                          <TextField
                            variant="standard"
                            fullWidth
                            sx={{ minWidth: 110 }}
                            value={expense.comments || ''}
                            onChange={(e) => handleInputChange(index, 'comments', e.target.value)}
                            placeholder="Add comments"
                          />
                        </TableCell>
                        <TableCell>
                          <ButtonOutlined
                            onClick={() => openFileInNewTab(expense.file)}
                          >
                            <AttachmentIcon />
                          </ButtonOutlined>
                        </TableCell>
                        <TableCell>
                          <ButtonContained
                            onClick={() => handleDeleteExpense(index)}
                          >
                            <DeleteIcon />
                          </ButtonContained>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter sx={{ borderBottom: '2px solid #354a5f' }}>
                    <TableRow sx={{ backgroundColor: '#354a5f' }}>
                      <TableCell colSpan={5} sx={{ fontFamily: "SapFiori72, sans-serif", color: 'white', textAlign: 'right', fontSize: '14px', padding: '10px 0' }}>
                        Total Base Amount:
                      </TableCell>
                      <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", color: 'white', textAlign: 'right', fontSize: '14px' }}>
                        {expenses.reduce((total, expense) => total + (expense.currency ? Number(calculateBaseAmount(expense.expense_amount, expense.currency)) : 0), 0).toLocaleString('en-US')}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "SapFiori72, sans-serif", color: 'white', fontSize: '14px' }}>
                        {baseCurrency}
                      </TableCell>
                      <TableCell colSpan={4} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="contained"
                component="label"
                sx={{
                  gap: 1,
                  backgroundColor: '#354a5f',
                  '&:hover': { backgroundColor: '#2c3e50' }
                }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : <AddIcon />}
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleAddFile}
                />
              </Button>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ButtonOutlined onClick={handleCancel}>
                  Cancel
                </ButtonOutlined>
                <ButtonContained
                  onClick={addExpenses}
                  {...(submitting ? { disabled: true } : {})}
                >
                  {submitting ? <CircularProgress size={24} /> : 'Save Expenses'}
                </ButtonContained>
              </Box>
            </Box>
            <Dialog open={open} onClose={handleClose} PaperProps={{ style: { width: '400px' } }}>
              <DialogContent>
                <TextField
                  autoFocus
                  margin="dense"
                  id="name"
                  type="text"
                  fullWidth
                  multiline
                  rows={4}
                  maxRows={6}
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
              </DialogActions>
            </Dialog>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default AddExpense;